import { useEffect, useMemo, useState } from 'react';

import { Alert, Button, TextField, Typography } from '@/ui';

import {
  AssistantActionPage,
  AssistantCommandProblemAlert,
} from './AssistantActionPage';
import { useGetAssistantSchedulingContextQuery } from './api';
import { useAssistantCommand } from './useAssistantCommand';
import { useEndAssistantSessionOnUnauthorized } from './useAssistantSession';

import type { AssistantActionContext } from './AssistantActionPage';
import type { FC } from 'react';

import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import {
  mentoCalBookingDayHeading,
  MentoCalTimeList,
} from '@/components/MentoCal/TimeList';
import { MentoCalTimeSlotButton } from '@/components/MentoCal/TimeSlotButton';

// The same bound as the cancel reason; Moments refuses a longer one (MEN-5652).
const MAX_REASON_LENGTH = 1000;

const viewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const RescheduleForm: FC<AssistantActionContext> = ({
  appointment,
  memberId,
  memberName,
}) => {
  const timezone = useMemo(viewerTimezone, []);
  const context = useGetAssistantSchedulingContextQuery(
    { appointmentId: appointment.id, memberId, viewerTimezone: timezone },
    { refetchOnMountOrArgChange: true },
  );
  useEndAssistantSessionOnUnauthorized(context.error);
  const { isChecking, problem, submit } = useAssistantCommand({
    action: 'reschedule',
    memberId,
    seen: appointment,
  });
  const days = context.currentData?.days ?? [];
  const firstOpenDay = days.find((day) => day.enabled && day.slots.length > 0);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const shownDate = selectedDate || firstOpenDay?.date || '';
  const slots = days.find((day) => day.date === shownDate)?.slots ?? [];
  const selectedSlot = slots.find((slot) => slot.startAt === selectedStart);
  const { refetch: refetchContext } = context;

  // A changed appointment may also have changed which times are free.
  useEffect(() => {
    if (problem?.kind === 'changed') {
      setSelectedStart(null);
      refetchContext();
    }
  }, [problem, refetchContext]);

  if (context.isError) {
    return (
      <Alert severity="error">
        We couldn&apos;t load available times. Go back and try again in a
        moment.
      </Alert>
    );
  }
  if (!context.currentData) {
    return <Typography variant="body1">Loading available times…</Typography>;
  }
  if (!context.currentData.eligible || !firstOpenDay) {
    return (
      <Alert severity="info">
        There are no times to move this session to online. Email
        concierge@mento.co and we&apos;ll help.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h5">
        Choose a new time for {memberName}&apos;s session
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Only this session moves; the rest of a recurring series stays as it is.
        Times are in {context.currentData.timezone}.
      </Typography>
      <div className="flex flex-col gap-6 md:flex-row">
        <MentoCalDatePicker
          days={days}
          selectedDate={shownDate}
          onSelectDate={(date) => {
            setSelectedDate(date);
            setSelectedStart(null);
          }}
        />
        <MentoCalTimeList
          className="flex-1"
          heading={mentoCalBookingDayHeading(shownDate)}
          scrollClassName="max-h-80"
        >
          {slots.map((slot) => (
            <MentoCalTimeSlotButton
              key={slot.startAt}
              label={slot.label}
              selected={slot.startAt === selectedStart}
              onClick={() => setSelectedStart(slot.startAt)}
            />
          ))}
        </MentoCalTimeList>
      </div>
      <TextField
        inputProps={{ maxLength: MAX_REASON_LENGTH }}
        label="Reason (optional)"
        minRows={2}
        value={reason}
        fullWidth
        multiline
        onChange={(event) => setReason(event.target.value)}
      />
      <AssistantCommandProblemAlert action="reschedule" problem={problem} />
      <div>
        <Button
          disabled={!selectedSlot || isChecking}
          variant="contained"
          onClick={() => {
            if (!selectedSlot) return;
            // A blank note is no note: the key is left off (MEN-5652).
            submit({
              start_at: selectedSlot.startAt,
              viewer_timezone: timezone,
              ...(reason.trim() ? { reason: reason.trim() } : {}),
            });
          }}
        >
          {selectedSlot
            ? `Reschedule to ${mentoCalBookingDayHeading(shownDate)}, ${selectedSlot.label}`
            : 'Choose a time'}
        </Button>
      </div>
    </div>
  );
};

/** `/assistant/members/:memberId/appointments/:appointmentId/reschedule` */
const AssistantReschedule: FC = () => (
  <AssistantActionPage action="reschedule">
    {(context) => <RescheduleForm {...context} />}
  </AssistantActionPage>
);

export default AssistantReschedule;
